// src/components/Responsabile/ResponsabileWorkerRequests.jsx - Gestione richieste per dipendenti
import React, { useState, useEffect } from 'react';
import { auth, getUserLeaveRequests, getAllUsers } from '../../firebase';
import ResponsabileRequestForm from './ResponsabileRequestForm';
import Notification from '../Notification';
import './ResponsabileWorkerRequests.css';

const ResponsabileWorkerRequests = () => {
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success'
  });
  
  // Stati per l'autocompletamento
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedUserData, setSelectedUserData] = useState(null);

  // Carica tutti gli utenti (esclusi admin e responsabili)
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const allUsers = await getAllUsers();
        const workers = allUsers.filter(user => 
          user.role === 'user' || !user.role
        ).sort((a, b) => {
          const aName = a.nome && a.cognome ? `${a.nome} ${a.cognome}` : a.email;
          const bName = b.nome && b.cognome ? `${b.nome} ${b.cognome}` : b.email;
          return aName.localeCompare(bName);
        });
        setUsers(workers);
        
        // Non selezionare automaticamente un utente
        // L'utente dovrà cercare e selezionare manualmente
      } catch (error) {
        console.error("ResponsabileWorkerRequests: Errore nel caricamento utenti:", error);
        setError("Errore nel caricamento degli utenti");
      }
    };
    loadUsers();
  }, []);

  // Filtra gli utenti in base al termine di ricerca
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers([]);
      setShowSuggestions(false);
      return;
    }

    const filtered = users.filter(user => {
      const fullName = user.nome && user.cognome 
        ? `${user.nome} ${user.cognome}`.toLowerCase()
        : user.email.toLowerCase();
      
      return fullName.includes(searchTerm.toLowerCase());
    });

    setFilteredUsers(filtered);
    setShowSuggestions(true);
  }, [searchTerm, users]);

  // Carica le richieste quando cambia l'utente selezionato
  useEffect(() => {
    const loadRequests = async () => {
      if (!selectedUser) {
        setRequests([]);
        setIsLoading(false);
        return;
      }
      
      console.log(`ResponsabileWorkerRequests: Caricamento richieste per userId=${selectedUser}`);
      setIsLoading(true);
      
      try {
        const userRequests = await getUserLeaveRequests(selectedUser);
        console.log("ResponsabileWorkerRequests: Richieste caricate", userRequests);
        setRequests(userRequests);
        setError(null);
      } catch (err) {
        console.error("ResponsabileWorkerRequests: Errore nel caricamento delle richieste:", err);
        setError("Impossibile caricare le richieste. Riprova più tardi.");
        setRequests([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadRequests();
  }, [selectedUser]);

  // Gestisce l'invio di una nuova richiesta
  const handleRequestSubmit = async (requestData) => {
    // Ricarica le richieste per l'utente selezionato
    try {
      const userRequests = await getUserLeaveRequests(selectedUser);
      setRequests(userRequests);
    } catch (error) {
      console.error("Errore nel ricaricare le richieste:", error);
    }
    
    // Mostra notifica di successo
    const selectedUserData = users.find(u => u.id === selectedUser);
    const userName = selectedUserData ? 
      (selectedUserData.nome && selectedUserData.cognome ? 
        `${selectedUserData.nome} ${selectedUserData.cognome}` : 
        selectedUserData.email) : 'utente';
        
    setNotification({
      show: true,
      message: `Richiesta creata con successo per ${userName}!`,
      type: 'success'
    });
    
    // Nascondi la notifica dopo 4 secondi
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 4000);
  };

  // Gestisce il cambio utente
  const handleUserChange = (event) => {
    setSelectedUser(event.target.value);
  };

  // Gestisce la selezione di un dipendente dalla ricerca
  const handleUserSelect = (user) => {
    const displayName = user.nome && user.cognome 
      ? `${user.nome} ${user.cognome}`
      : user.email;
    
    setSearchTerm(displayName);
    setSelectedUser(user.id);
    setSelectedUserData(user);
    setShowSuggestions(false);
  };

  // Gestisce il cambio del termine di ricerca
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (value.trim() === '') {
      setSelectedUser('');
      setSelectedUserData(null);
    }
  };

  // Gestisce il blur del campo di ricerca
  const handleSearchBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  // Formatta la data in formato leggibile
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Ottieni il nome del tipo di richiesta
  const getRequestTypeName = (type) => {
    switch (type) {
      case 'permission':
        return 'Permesso';
      case 'vacation':
        return 'Ferie';
      case 'sickness':
        return 'Malattia';
      default:
        return type;
    }
  };

  // Ottieni il nome dello stato della richiesta
  const getStatusName = (status) => {
    switch (status) {
      case 'pending':
        return 'In attesa';
      case 'approved':
        return 'Approvata';
      case 'rejected':
        return 'Rifiutata';
      default:
        return status;
    }
  };

  // Calcola i giorni tra due date
  const calculateDaysBetween = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    
    const diffTime = Math.abs(end - start);
    return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  // Ottieni la classe CSS per lo stato
  const getStatusClass = (status) => {
    switch (status) {
      case 'pending':
        return 'status-pending';
      case 'approved':
        return 'status-approved';
      case 'rejected':
        return 'status-rejected';
      default:
        return '';
    }
  };

  // Ottieni il nome dell'utente selezionato
  const getSelectedUserName = () => {
    if (selectedUserData) {
      return selectedUserData.nome && selectedUserData.cognome ? 
        `${selectedUserData.nome} ${selectedUserData.cognome}` : 
        selectedUserData.email;
    }
    
    const user = users.find(u => u.id === selectedUser);
    if (!user) return '';
    return user.nome && user.cognome ? 
      `${user.nome} ${user.cognome}` : 
      user.email;
  };

  return (
    <div className="card">
      <div className="card-header">
        <div>Gestione Richieste Dipendenti</div>
        <button 
          className="btn btn-primary"
          onClick={() => setShowRequestForm(true)}
          disabled={!selectedUser}
          style={{
            backgroundColor: '#e67e22',
            borderColor: '#e67e22'
          }}
        >
          Nuova richiesta
        </button>
      </div>
      
      <div className="card-body">
        {/* Notifiche */}
        {notification.show && (
          <Notification
            message={notification.message}
            isVisible={notification.show}
            onClose={() => setNotification(prev => ({ ...prev, show: false }))}
            type={notification.type}
          />
        )}

        {/* Selettore utente con ricerca */}
        <div style={{
          background: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '20px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '15px',
            flexWrap: 'wrap',
            marginBottom: '15px'
          }}>
            <label htmlFor="user-search" style={{
              fontWeight: '600',
              color: '#856404',
              minWidth: '120px'
            }}>
              👤 Cerca Dipendente:
            </label>
            
            {/* Campo di ricerca con autocompletamento */}
            <div style={{ position: 'relative', flex: '1', minWidth: '250px' }}>
              <input
                id="user-search"
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={() => searchTerm && setShowSuggestions(true)}
                onBlur={handleSearchBlur}
                placeholder="Digita nome e cognome..."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ffeaa7',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  fontSize: '14px'
                }}
              />
              
              {/* Lista suggerimenti */}
              {showSuggestions && filteredUsers.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ffeaa7',
                  borderTop: 'none',
                  borderRadius: '0 0 4px 4px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  zIndex: 1000,
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {filteredUsers.map(user => (
                    <div
                      key={user.id}
                      onClick={() => handleUserSelect(user)}
                      style={{
                        padding: '10px 15px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f9f9f9',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      <div style={{ fontWeight: 'bold', color: '#2c3e50' }}>
                        {user.nome && user.cognome 
                          ? `${user.nome} ${user.cognome}`
                          : user.email}
                      </div>
                      <small style={{ color: '#6c757d' }}>
                        {user.email}
                      </small>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Selettore tradizionale come backup */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            paddingTop: '10px',
            borderTop: '1px solid #f0e68c'
          }}>
            <select
              id="user-select"
              value={selectedUser}
              onChange={handleUserChange}
              style={{
                padding: '6px 10px',
                border: '1px solid #ffeaa7',
                borderRadius: '4px',
                backgroundColor: 'white',
                minWidth: '250px',
                fontSize: '13px'
              }}
            >
              <option value="">-- Seleziona un dipendente --</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.nome && user.cognome 
                    ? `${user.nome} ${user.cognome} (${user.email})`
                    : user.email}
                </option>
              ))}
            </select>
          </div>
          
          {selectedUser && (
            <div style={{
              marginTop: '12px',
              fontSize: '14px',
              color: '#856404',
              fontStyle: 'italic',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '16px' }}>✅</span>
              <span>Dipendente selezionato: <strong>{getSelectedUserName()}</strong></span>
            </div>
          )}
        </div>
        
        {/* Contenuto principale */}
        {!selectedUser ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#6c757d'
          }}>
            <h3>🔍 Cerca un dipendente</h3>
            <p>Utilizza la barra di ricerca sopra per trovare un dipendente</p>
            <p>oppure selezionalo dall'elenco dropdown</p>
            <div style={{
              marginTop: '20px',
              padding: '15px',
              background: '#f8f9fa',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#495057'
            }}>
              💡 <strong>Suggerimento:</strong> Digita il nome o cognome del dipendente per una ricerca rapida
            </div>
          </div>
        ) : isLoading ? (
          <div className="loading">Caricamento richieste in corso...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : (
          <div className="table-responsive">
            <table id="user-requests-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Data</th>
                  <th>Dettagli</th>
                  <th>Stato</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center">
                      {getSelectedUserName()} non ha ancora fatto nessuna richiesta
                    </td>
                  </tr>
                ) : (
                  requests.map((request) => (
                    <tr key={request.id}>
                      <td>{getRequestTypeName(request.type)}</td>
                      <td>
                        {formatDate(request.dateFrom)}
                        {(request.dateTo && (request.type === 'vacation' || request.permissionType === 'multi-day')) && 
                          ` - ${formatDate(request.dateTo)}`}
                      </td>
                      <td>
                        {/* Dettagli specifici per tipo di richiesta */}
                        {request.type === 'permission' && request.permissionType === 'hourly' && (
                          <>
                            Orario: {request.timeFrom} - {request.timeTo}
                          </>
                        )}
                        {request.type === 'permission' && request.permissionType === 'daily' && (
                          'Giornata intera'
                        )}
                        {request.type === 'permission' && request.permissionType === 'multi-day' && (
                          <>
                            <div>Multi-giorni</div>
                            {request.workingDaysCount && (
                              <small className="text-muted">
                                {request.workingDaysCount} giorni lavorativi
                              </small>
                            )}
                          </>
                        )}
                        {request.type === 'vacation' && (
                          <>
                            <div>Ferie</div>
                            {request.dateFrom && request.dateTo && (
                              <small className="text-muted">
                                {calculateDaysBetween(request.dateFrom, request.dateTo)} giorni
                              </small>
                            )}
                          </>
                        )}
                        {request.type === 'sickness' && (
                          <>
                            <div>Malattia</div>
                            <small className="text-muted">
                              Protocollo: {request.protocolCode}
                            </small>
                            {request.dateTo && (
                              <small className="text-muted d-block">
                                Periodo: {formatDate(request.dateFrom)} - {formatDate(request.dateTo)}
                              </small>
                            )}
                          </>
                        )}
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusClass(request.status)}`}>
                          {getStatusName(request.status)}
                        </span>
                        {/* Indicatore sincronizzazione per richieste approvate */}
                        {request.status === 'approved' && request.syncInfo && (
                          <div className="sync-status-indicator">
                            {request.syncInfo.syncResult ? (
                              <small className="text-success">
                                ✅ Sincronizzato ({request.syncInfo.syncDetails?.totalDates || 0} date)
                              </small>
                            ) : (
                              <small className="text-warning">
                                ⚠️ Errore sincronizzazione
                              </small>
                            )}
                          </div>
                        )}
                      </td>
                      <td>{request.adminNotes || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Form per l'aggiunta di nuove richieste */}
      <ResponsabileRequestForm 
        isVisible={showRequestForm} 
        onClose={() => setShowRequestForm(false)}
        onSubmit={handleRequestSubmit}
        selectedUserId={selectedUser}
        selectedUserName={getSelectedUserName()}
      />
    </div>
  );
};

export default ResponsabileWorkerRequests;